/**
 * Extra static verticals for template preview (`previewCatalogForStoreTemplateId`).
 * Kept separate from `demoStorefrontData.ts` to avoid a single enormous module.
 */
import type { Category } from '@/services/categories'
import type { Product } from '@/services/products'
import { assertPreviewVerticalProductCategoryCohesion } from '@/data/previewCatalogIntegrity'
import { enrichFashionVerticalProduct } from '@/data/previewFashionDemoVariants'

const DEMO_STORE_ID = '__preview__'
const NOW = '2024-01-01T00:00:00Z'

function cat(p: Partial<Category> & Pick<Category, 'id' | 'name' | 'slug' | 'position'>): Category {
  return {
    store_id: DEMO_STORE_ID,
    parent_id: null,
    description: p.description ?? null,
    image_url: p.image_url ?? null,
    is_active: true,
    created_at: NOW,
    updated_at: NOW,
    product_count: p.product_count ?? 2,
    ...p,
  }
}

function prod(p: Partial<Product> & Pick<Product, 'id' | 'name' | 'slug' | 'price' | 'category_id'>): Product {
  return {
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    compare_at_price: null,
    stock: 20,
    status: 'active',
    image: '/templates/local-webp/img-48bf995312.webp',
    images: ['/templates/local-webp/img-48bf995312.webp'],
    orders_count: 10,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    product_type: 'other',
    ...p,
  }
}

// ─── Fashion (base `fashion` + editorial runway slugs) ─────────────────────

const FC_NEW = 'f0000001-0000-4000-8000-000000000001'
const FC_FEMME = 'f0000001-0000-4000-8000-000000000002'
const FC_HOMME = 'f0000001-0000-4000-8000-000000000003'
const FC_ACC = 'f0000001-0000-4000-8000-000000000004'
const FC_SOLDES = 'f0000001-0000-4000-8000-000000000005'

export const demoCategoriesFashion: Category[] = [
  cat({
    id: FC_NEW,
    name: 'Nouveautés',
    slug: 'nouveautes',
    position: 1,
    description: 'Dernières pièces arrivées en studio',
    image_url: '/templates/fashion-v2-gen/fv2-gen-cat-nouveau.webp',
  }),
  cat({
    id: FC_FEMME,
    name: 'Femme',
    slug: 'femme',
    position: 2,
    description: 'Silhouettes et matières premium',
    image_url: '/templates/fashion-v2-gen/fv2-gen-cat-femme.webp',
  }),
  cat({
    id: FC_HOMME,
    name: 'Homme',
    slug: 'homme',
    position: 3,
    description: 'Essentiels et pièces fortes',
    image_url: '/templates/fashion-v2-gen/fv2-gen-cat-homme.webp',
  }),
  cat({
    id: FC_ACC,
    name: 'Accessoires',
    slug: 'accessoires',
    position: 4,
    description: 'Ceintures, sacs, foulards',
    image_url: '/templates/fashion-v2-gen/fv2-gen-cat-acc.webp',
  }),
  cat({
    id: FC_SOLDES,
    name: 'Soldes',
    slug: 'soldes',
    position: 5,
    description: 'Dernières tailles — prix réduits',
    image_url: '/templates/fashion-v2-gen/fv2-gen-cat-soldes.webp',
  }),
]

const demoProductsFashionBase: Product[] = [
  prod({
    id: 'fsh-prod-1',
    name: 'Manteau laine — Minuit',
    slug: 'atelier-manteau-laine-minuit',
    price: 420,
    category_id: FC_NEW,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-coat-minuit.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-coat-minuit.webp'],
  }),
  prod({
    id: 'fsh-prod-2',
    name: 'Blazer structuré — Ivoire',
    slug: 'atelier-blazer-structure-ivoire',
    price: 289,
    category_id: FC_FEMME,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-blazer-ivoire.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-blazer-ivoire.webp'],
  }),
  prod({
    id: 'fsh-prod-3',
    name: 'Chemise popeline — Graphite',
    slug: 'atelier-chemise-popeline-graphite',
    price: 119,
    category_id: FC_HOMME,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-mens-shirt.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-mens-shirt.webp'],
  }),
  prod({
    id: 'fsh-prod-4',
    name: 'Sac bandoulière — Cuir grainé',
    slug: 'atelier-sac-bandouliere-cuir',
    price: 210,
    category_id: FC_ACC,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-handbag.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-handbag.webp'],
  }),
  prod({
    id: 'fsh-prod-5',
    name: 'Ceinture tressée — Noir',
    slug: 'atelier-ceinture-tressee-noir',
    price: 59,
    category_id: FC_ACC,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-belt-black.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-belt-black.webp'],
  }),
  prod({
    id: 'fsh-prod-6',
    name: 'Robe satin — Olive',
    slug: 'atelier-robe-satin-olive',
    price: 198,
    compare_at_price: 245,
    category_id: FC_SOLDES,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-robe-olive.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-robe-olive.webp'],
  }),
  prod({
    id: 'fsh-prod-7',
    name: 'Pantalon plissé — Sable',
    slug: 'atelier-pantalon-plisse-sable',
    price: 145,
    category_id: FC_FEMME,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-pants-sand.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-pants-sand.webp'],
  }),
  prod({
    id: 'fsh-prod-8',
    name: 'Sneakers cuir — Craie',
    slug: 'atelier-sneakers-cuir-craie',
    price: 179,
    category_id: FC_NEW,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-sneakers-ivory.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-sneakers-ivory.webp'],
  }),
]

export const demoProductsFashion: Product[] = demoProductsFashionBase.map(enrichFashionVerticalProduct)

// ─── Fashion v2 (`fashionv2` template) — same category slugs, distinct SKUs vs `fashion`. ─

const FV2_NEW = 'f2000001-0000-4000-8000-000000000001'
const FV2_FEMME = 'f2000001-0000-4000-8000-000000000002'
const FV2_HOMME = 'f2000001-0000-4000-8000-000000000003'
const FV2_ACC = 'f2000001-0000-4000-8000-000000000004'
const FV2_SOLDES = 'f2000001-0000-4000-8000-000000000005'

export const demoCategoriesFashionV2: Category[] = [
  cat({
    id: FV2_NEW,
    name: 'Nouveautés',
    slug: 'nouveautes',
    position: 1,
    description: 'Capsule studio — pièces limitées',
    image_url: '/templates/fashion-v2-gen/fv2-gen-story-01.webp',
  }),
  cat({
    id: FV2_FEMME,
    name: 'Femme',
    slug: 'femme',
    position: 2,
    description: 'Silhouettes éditoriales',
    image_url: '/templates/fashion-v2-gen/fv2-gen-cat-femme-v2.webp',
  }),
  cat({
    id: FV2_HOMME,
    name: 'Homme',
    slug: 'homme',
    position: 3,
    description: 'Essentiels urbains',
    image_url: '/templates/fashion-v2-gen/fv2-gen-story-03.webp',
  }),
  cat({
    id: FV2_ACC,
    name: 'Accessoires',
    slug: 'accessoires',
    position: 4,
    description: 'Ceintures et maroquinerie',
    image_url: '/templates/fashion-v2-gen/fv2-gen-story-06.webp',
  }),
  cat({
    id: FV2_SOLDES,
    name: 'Soldes',
    slug: 'soldes',
    position: 5,
    description: 'Dernières tailles',
    image_url: '/templates/fashion-v2-gen/fv2-gen-story-07.webp',
  }),
]

const demoProductsFashionV2Base: Product[] = [
  prod({
    id: 'fv2-prod-1',
    name: 'Trench imperméable — Encre',
    slug: 'studio-trench-impermeable-encre',
    price: 398,
    category_id: FV2_NEW,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-trench.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-trench.webp'],
  }),
  prod({
    id: 'fv2-prod-2',
    name: 'Cardigan alpaga — Brume',
    slug: 'studio-cardigan-alpaga-brume',
    price: 265,
    category_id: FV2_FEMME,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-cardigan.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-cardigan.webp'],
  }),
  prod({
    id: 'fv2-prod-3',
    name: 'Polo maille — Silex',
    slug: 'studio-polo-maille-silex',
    price: 98,
    category_id: FV2_HOMME,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-polo.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-polo.webp'],
  }),
  prod({
    id: 'fv2-prod-4',
    name: 'Sac banane cuir — Noir',
    slug: 'studio-sac-banane-cuir-noir',
    price: 189,
    category_id: FV2_ACC,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-banana-bag.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-banana-bag.webp'],
  }),
  prod({
    id: 'fv2-prod-5',
    name: 'Ceinture minimaliste — Bronze',
    slug: 'studio-ceinture-minimal-bronze',
    price: 72,
    category_id: FV2_ACC,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-belt-bronze.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-belt-bronze.webp'],
  }),
  prod({
    id: 'fv2-prod-6',
    name: 'Robe longue satin — Aubergine',
    slug: 'studio-robe-longue-satin-aubergine',
    price: 228,
    compare_at_price: 290,
    category_id: FV2_SOLDES,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-gown-aub.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-gown-aub.webp'],
  }),
  prod({
    id: 'fv2-prod-7',
    name: 'Jupe plissée — Acier',
    slug: 'studio-jupe-plissee-acier',
    price: 134,
    category_id: FV2_FEMME,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-skirt-steel.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-skirt-steel.webp'],
  }),
  prod({
    id: 'fv2-prod-8',
    name: 'Baskets cuir — Sel marin',
    slug: 'studio-baskets-cuir-sel-marin',
    price: 195,
    category_id: FV2_NEW,
    product_type: 'fashion',
    image: '/templates/fashion-v2-gen/fv2-gen-prod-sneakers-selm.webp',
    images: ['/templates/fashion-v2-gen/fv2-gen-prod-sneakers-selm.webp'],
  }),
]

export const demoProductsFashionV2: Product[] = demoProductsFashionV2Base.map(enrichFashionVerticalProduct)

// ─── Editorial (premium-sleek-editorial PDP slugs) ───────────────────────────

const ED_APP = 'e0000001-0000-4000-8000-000000000001'
const ED_ACC = 'e0000001-0000-4000-8000-000000000002'
const ED_LEA = 'e0000001-0000-4000-8000-000000000003'
const ED_NEW = 'e0000001-0000-4000-8000-000000000004'

export const demoCategoriesEditorial: Category[] = [
  cat({ id: ED_APP, name: 'Horlogerie', slug: 'horlogerie', position: 1, image_url: '/templates/editorial-fashion-v1/vis-hero-main-800.webp' }),
  cat({ id: ED_ACC, name: 'Accessoires', slug: 'accessoires', position: 2, image_url: '/templates/editorial-fashion-v1/vis-promo-a-800.webp' }),
  cat({ id: ED_LEA, name: 'Maroquinerie', slug: 'maroquinerie', position: 3, image_url: '/templates/editorial-fashion-v1/vis-promo-b-800.webp' }),
  cat({ id: ED_NEW, name: 'Nouveautés', slug: 'nouveautes', position: 4, image_url: '/templates/editorial-fashion-v1/vis-shop-banner-800.webp' }),
]

const demoProductsEditorialBase: Product[] = [
  prod({
    id: 'edt-prod-1',
    name: '01 — Montre : cadran noir, aiguilles & index or rose',
    slug: 'montre-acier-minuit-40',
    price: 2890,
    category_id: ED_APP,
    product_type: 'fashion',
    image: '/templates/editorial-fashion-v1/vis-edt-01-800.webp',
    images: ['/templates/editorial-fashion-v1/vis-edt-01-800.webp'],
  }),
  prod({
    id: 'edt-prod-2',
    name: '02 — Bracelet montre : cuir grainé & boucle déployante',
    slug: 'chronographe-sablier-42',
    price: 4520,
    category_id: ED_APP,
    product_type: 'fashion',
    image: '/templates/editorial-fashion-v1/vis-edt-02-800.webp',
    images: ['/templates/editorial-fashion-v1/vis-edt-02-800.webp'],
  }),
  prod({
    id: 'edt-prod-3',
    name: '03 — Montre : profil boîtier brossé, anses & bracelet cuir',
    slug: 'boitier-brosse-mesure-40',
    price: 2380,
    category_id: ED_APP,
    product_type: 'fashion',
    image: '/templates/editorial-fashion-v1/vis-edt-03-800.webp',
    images: ['/templates/editorial-fashion-v1/vis-edt-03-800.webp'],
  }),
  prod({
    id: 'edt-prod-4',
    name: '04 — Détail montre : verre saphir bombé & lunette polie',
    slug: 'verre-saphir-bombe-clarte',
    price: 890,
    category_id: ED_APP,
    product_type: 'fashion',
    image: '/templates/editorial-fashion-v1/vis-edt-04-800.webp',
    images: ['/templates/editorial-fashion-v1/vis-edt-04-800.webp'],
  }),
  prod({
    id: 'edt-prod-5',
    name: '05 — Montre mécanique : coffret, coussin & nature morte',
    slug: 'tourbillon-lumen-ecrin',
    price: 14800,
    category_id: ED_APP,
    product_type: 'fashion',
    image: '/templates/editorial-fashion-v1/vis-edt-05-800.webp',
    images: ['/templates/editorial-fashion-v1/vis-edt-05-800.webp'],
  }),
  prod({
    id: 'edt-prod-6',
    name: '06 — Montre portée : bracelet cuir, boîtier au poignet',
    slug: 'montre-petite-seconde-manche-38',
    price: 2650,
    category_id: ED_APP,
    product_type: 'fashion',
    image: '/templates/editorial-fashion-v1/vis-edt-06-800.webp',
    images: ['/templates/editorial-fashion-v1/vis-edt-06-800.webp'],
  }),
]

export const demoProductsEditorial: Product[] = demoProductsEditorialBase.map(enrichFashionVerticalProduct)

// ─── Editorial sleek (`premium-sleek-editorial-v1`) — distinct SKUs vs runway editorial. ─

const ESK_APP = 'e3000001-0000-4000-8000-000000000001'
const ESK_ACC = 'e3000001-0000-4000-8000-000000000002'
const ESK_LEA = 'e3000001-0000-4000-8000-000000000003'
const ESK_NEW = 'e3000001-0000-4000-8000-000000000004'

export const demoCategoriesEditorialSleek: Category[] = [
  cat({ id: ESK_APP, name: 'Prêt-à-porter', slug: 'apparel', position: 1, image_url: '/templates/premium-sleek-editorial-v1/sleek-cat-apparel-800.webp' }),
  cat({ id: ESK_ACC, name: 'Accessoires', slug: 'accessories', position: 2, image_url: '/templates/premium-sleek-editorial-v1/sleek-cat-accessoires-800.webp' }),
  cat({ id: ESK_LEA, name: 'Maroquinerie', slug: 'leather-goods', position: 3, image_url: '/templates/premium-sleek-editorial-v1/sleek-cat-maroquinerie-800.webp' }),
  cat({ id: ESK_NEW, name: 'Capsules', slug: 'nouveautes', position: 4, image_url: '/templates/premium-sleek-editorial-v1/sleek-cat-capsules-800.webp' }),
]

const demoProductsEditorialSleekBase: Product[] = [
  prod({
    id: 'esk-prod-1',
    name: 'Manteau Silhouette',
    slug: 'silhouette-manteau-01',
    price: 920,
    category_id: ESK_APP,
    product_type: 'fashion',
    image: '/templates/premium-sleek-editorial-v1/silhouette-manteau-01-800.webp',
    images: ['/templates/premium-sleek-editorial-v1/silhouette-manteau-01-800.webp'],
  }),
  prod({
    id: 'esk-prod-2',
    name: 'Blazer Structuré',
    slug: 'blazer-structure-studio',
    price: 540,
    category_id: ESK_APP,
    product_type: 'fashion',
    image: '/templates/premium-sleek-editorial-v1/blazer-structure-studio-800.webp',
    images: ['/templates/premium-sleek-editorial-v1/blazer-structure-studio-800.webp'],
  }),
  prod({
    id: 'esk-prod-3',
    name: 'Ensemble minimal trois pièces',
    slug: 'ensemble-minimal-trois-pieces',
    price: 430,
    category_id: ESK_LEA,
    product_type: 'fashion',
    image: '/templates/premium-sleek-editorial-v1/ensemble-minimal-trois-pieces-800.webp',
    images: ['/templates/premium-sleek-editorial-v1/ensemble-minimal-trois-pieces-800.webp'],
  }),
  prod({
    id: 'esk-prod-4',
    name: 'Bandeau soie Minuit',
    slug: 'bandeau-soie-minuit-sleek',
    price: 88,
    category_id: ESK_ACC,
    product_type: 'fashion',
    image: '/templates/premium-sleek-editorial-v1/bandeau-soie-minuit-sleek-800.webp',
    images: ['/templates/premium-sleek-editorial-v1/bandeau-soie-minuit-sleek-800.webp'],
  }),
  prod({
    id: 'esk-prod-5',
    name: 'Manchette rigide argent',
    slug: 'manchette-rigide-argent',
    price: 74,
    category_id: ESK_ACC,
    product_type: 'fashion',
    image: '/templates/premium-sleek-editorial-v1/manchette-rigide-argent-800.webp',
    images: ['/templates/premium-sleek-editorial-v1/manchette-rigide-argent-800.webp'],
  }),
  prod({
    id: 'esk-prod-6',
    name: 'Jupe midi crêpe Perle',
    slug: 'jupe-midi-crepe-perle',
    price: 255,
    category_id: ESK_APP,
    product_type: 'fashion',
    image: '/templates/premium-sleek-editorial-v1/jupe-midi-crepe-perle-800.webp',
    images: ['/templates/premium-sleek-editorial-v1/jupe-midi-crepe-perle-800.webp'],
  }),
  prod({
    id: 'esk-prod-7',
    name: 'Mitaines cachemire Fumée',
    slug: 'mitaines-cachemire-fumee',
    price: 118,
    category_id: ESK_ACC,
    product_type: 'fashion',
    image: '/templates/premium-sleek-editorial-v1/mitaines-cachemire-fumee-800.webp',
    images: ['/templates/premium-sleek-editorial-v1/mitaines-cachemire-fumee-800.webp'],
  }),
  prod({
    id: 'esk-prod-8',
    name: 'Béret laine Sienne',
    slug: 'beret-laine-sienne',
    price: 64,
    category_id: ESK_NEW,
    product_type: 'fashion',
    image: '/templates/premium-sleek-editorial-v1/beret-laine-sienne-800.webp',
    images: ['/templates/premium-sleek-editorial-v1/beret-laine-sienne-800.webp'],
  }),
]

export const demoProductsEditorialSleek: Product[] = demoProductsEditorialSleekBase.map(enrichFashionVerticalProduct)

// ─── Food ────────────────────────────────────────────────────────────────────

const FD_FR = 'fd000001-0000-4000-8000-000000000001'
const FD_LA = 'fd000001-0000-4000-8000-000000000002'
const FD_EP = 'fd000001-0000-4000-8000-000000000003'
const FD_BO = 'fd000001-0000-4000-8000-000000000004'

export const demoCategoriesFood: Category[] = [
  cat({
    id: FD_FR,
    name: 'Fruits & Légumes',
    slug: 'fruits-legumes',
    position: 1,
    image_url: '/templates/preview-food/fd-cat-fruits.webp',
  }),
  cat({
    id: FD_LA,
    name: 'Produits laitiers',
    slug: 'laitiers',
    position: 2,
    image_url: '/templates/preview-food/fd-cat-laitiers.webp',
  }),
  cat({
    id: FD_EP,
    name: 'Épicerie fine',
    slug: 'epicerie',
    position: 3,
    image_url: '/templates/preview-food/fd-cat-epicerie.webp',
  }),
  cat({
    id: FD_BO,
    name: 'Boissons',
    slug: 'boissons',
    position: 4,
    image_url: '/templates/preview-food/fd-cat-boissons.webp',
  }),
]

export const demoProductsFood: Product[] = [
  prod({
    id: 'fd-prod-1',
    name: 'Panier tomates cerises',
    slug: 'fd-grocery-tomates-cerises-500g',
    price: 6.5,
    category_id: FD_FR,
    product_type: 'other',
    image: '/templates/preview-food/fd-prod-tomates.webp',
    images: ['/templates/preview-food/fd-prod-tomates.webp'],
  }),
  prod({
    id: 'fd-prod-2',
    name: 'Salade mesclun lavée',
    slug: 'fd-grocery-salade-mesclun-250g',
    price: 4.2,
    category_id: FD_FR,
    product_type: 'other',
    image: '/templates/preview-food/fd-prod-mesclun.webp',
    images: ['/templates/preview-food/fd-prod-mesclun.webp'],
  }),
  prod({
    id: 'fd-prod-3',
    name: 'Yaourt fermier nature',
    slug: 'fd-grocery-yaourt-fermier-nature',
    price: 1.8,
    category_id: FD_LA,
    product_type: 'other',
    image: '/templates/preview-food/fd-prod-yaourt.webp',
    images: ['/templates/preview-food/fd-prod-yaourt.webp'],
  }),
  prod({
    id: 'fd-prod-4',
    name: 'Fromage brebis affiné',
    slug: 'fd-grocery-fromage-brebis-200g',
    price: 18.9,
    category_id: FD_LA,
    product_type: 'other',
    image: '/templates/preview-food/fd-prod-fromage.webp',
    images: ['/templates/preview-food/fd-prod-fromage.webp'],
  }),
  prod({
    id: 'fd-prod-5',
    name: 'Huile d’olive extra vierge',
    slug: 'fd-grocery-huile-olive-500ml',
    price: 24,
    category_id: FD_EP,
    product_type: 'other',
    image: '/templates/preview-food/fd-prod-huile.webp',
    images: ['/templates/preview-food/fd-prod-huile.webp'],
  }),
  prod({
    id: 'fd-prod-6',
    name: 'Miel de montagne',
    slug: 'fd-grocery-miel-montagne-350g',
    price: 32,
    category_id: FD_EP,
    product_type: 'other',
    image: '/templates/preview-food/fd-prod-miel.webp',
    images: ['/templates/preview-food/fd-prod-miel.webp'],
  }),
  prod({
    id: 'fd-prod-7',
    name: 'Jus d’orange pressé 1L',
    slug: 'fd-grocery-jus-orange-1l',
    price: 8.5,
    category_id: FD_BO,
    product_type: 'other',
    image: '/templates/preview-food/fd-prod-jus.webp',
    images: ['/templates/preview-food/fd-prod-jus.webp'],
  }),
  prod({
    id: 'fd-prod-8',
    name: 'Eau minérale pack x6',
    slug: 'fd-grocery-eau-minerale-pack-6',
    price: 5.9,
    category_id: FD_BO,
    product_type: 'other',
    image: '/templates/preview-food/fd-prod-eau.webp',
    images: ['/templates/preview-food/fd-prod-eau.webp'],
  }),
]

// ─── Kids ────────────────────────────────────────────────────────────────────

const KD1 = 'kd000001-0000-4000-8000-000000000001'
const KD2 = 'kd000001-0000-4000-8000-000000000002'
const KD3 = 'kd000001-0000-4000-8000-000000000003'
const KD4 = 'kd000001-0000-4000-8000-000000000004'

export const demoCategoriesKids: Category[] = [
  cat({ id: KD1, name: '0–3 ans', slug: '0-3-ans', position: 1, image_url: '/templates/preview-kids/kid-cat-0-3.webp' }),
  cat({ id: KD2, name: '3–6 ans', slug: '3-6-ans', position: 2, image_url: '/templates/preview-kids/kid-cat-3-6.webp' }),
  cat({ id: KD3, name: '6–12 ans', slug: '6-12-ans', position: 3, image_url: '/templates/preview-kids/kid-cat-6-12.webp' }),
  cat({ id: KD4, name: 'Jeux éducatifs', slug: 'educatifs', position: 4, image_url: '/templates/preview-kids/kid-cat-educatif.webp' }),
]

export const demoProductsKids: Product[] = [
  prod({
    id: 'kd-prod-1',
    name: 'Hochet silicone — forêt',
    slug: 'kd-play-hochet-silicone-foret',
    price: 29,
    category_id: KD1,
    product_type: 'other',
    image: '/templates/preview-kids/kid-prod-hochet.webp',
    images: ['/templates/preview-kids/kid-prod-hochet.webp'],
  }),
  prod({
    id: 'kd-prod-2',
    name: 'Livre tissu découvertes',
    slug: 'kd-play-livre-tissu-decouvertes',
    price: 34,
    category_id: KD1,
    product_type: 'other',
    image: '/templates/preview-kids/kid-prod-livre-tissu.webp',
    images: ['/templates/preview-kids/kid-prod-livre-tissu.webp'],
  }),
  prod({
    id: 'kd-prod-3',
    name: 'Circuit bois — safari',
    slug: 'kd-play-circuit-bois-safari',
    price: 59,
    category_id: KD2,
    product_type: 'other',
    image: '/templates/preview-kids/kid-prod-circuit.webp',
    images: ['/templates/preview-kids/kid-prod-circuit.webp'],
  }),
  prod({
    id: 'kd-prod-4',
    name: 'Dînette inox — 12 pièces',
    slug: 'kd-play-dinette-inox-12',
    price: 44,
    category_id: KD2,
    product_type: 'other',
    image: '/templates/preview-kids/kid-prod-dinette.webp',
    images: ['/templates/preview-kids/kid-prod-dinette.webp'],
  }),
  prod({
    id: 'kd-prod-5',
    name: 'Robot à programmer',
    slug: 'kd-play-robot-programmer-stem',
    price: 129,
    category_id: KD4,
    product_type: 'other',
    image: '/templates/preview-kids/kid-prod-robot.webp',
    images: ['/templates/preview-kids/kid-prod-robot.webp'],
  }),
  prod({
    id: 'kd-prod-6',
    name: 'Kit microscope junior',
    slug: 'kd-play-kit-microscope-junior',
    price: 89,
    category_id: KD4,
    product_type: 'other',
    image: '/templates/preview-kids/kid-prod-microscope.webp',
    images: ['/templates/preview-kids/kid-prod-microscope.webp'],
  }),
  prod({
    id: 'kd-prod-7',
    name: 'Trottinette 3 roues',
    slug: 'kd-play-trottinette-3-roues',
    price: 165,
    category_id: KD3,
    product_type: 'other',
    image: '/templates/preview-kids/kid-prod-trottinette.webp',
    images: ['/templates/preview-kids/kid-prod-trottinette.webp'],
  }),
  prod({
    id: 'kd-prod-8',
    name: 'Ballon sensoriel',
    slug: 'kd-play-ballon-sensoriel',
    price: 22,
    category_id: KD3,
    product_type: 'other',
    image: '/templates/preview-kids/kid-prod-ballon.webp',
    images: ['/templates/preview-kids/kid-prod-ballon.webp'],
  }),
]

// ─── Pharmacy ───────────────────────────────────────────────────────────────

const PH1 = 'ph000001-0000-4000-8000-000000000001'
const PH2 = 'ph000001-0000-4000-8000-000000000002'
const PH3 = 'ph000001-0000-4000-8000-000000000003'
const PH4 = 'ph000001-0000-4000-8000-000000000004'

export const demoCategoriesPharmacy: Category[] = [
  cat({ id: PH1, name: 'Soins du corps', slug: 'corps', position: 1, image_url: '/templates/preview-pharmacy/prm-cat-corps.webp' }),
  cat({ id: PH2, name: 'Compléments', slug: 'complements', position: 2, image_url: '/templates/preview-pharmacy/prm-cat-complements.webp' }),
  cat({ id: PH3, name: 'Matériel médical', slug: 'medical', position: 3, image_url: '/templates/preview-pharmacy/prm-cat-medical.webp' }),
  cat({ id: PH4, name: 'Bébé & maternité', slug: 'bebe', position: 4, image_url: '/templates/preview-pharmacy/prm-cat-bebe.webp' }),
]

export const demoProductsPharmacy: Product[] = [
  prod({
    id: 'ph-prod-1',
    name: 'Magnésium bisglycinate 60 gélules',
    slug: 'ph-vital-magnesium-bisglycinate-60',
    price: 42,
    category_id: PH2,
    product_type: 'other',
    image: '/templates/preview-pharmacy/prm-prod-magnesium.webp',
    images: ['/templates/preview-pharmacy/prm-prod-magnesium.webp'],
  }),
  prod({
    id: 'ph-prod-2',
    name: 'Vitamine D3 2000 UI',
    slug: 'ph-vital-vitamine-d3-2000',
    price: 36,
    category_id: PH2,
    product_type: 'other',
    image: '/templates/preview-pharmacy/prm-prod-vitd.webp',
    images: ['/templates/preview-pharmacy/prm-prod-vitd.webp'],
  }),
  prod({
    id: 'ph-prod-3',
    name: 'Baume réparateur mains',
    slug: 'ph-vital-baume-reparateur-mains',
    price: 24,
    category_id: PH1,
    product_type: 'other',
    image: '/templates/preview-pharmacy/prm-prod-baume-mains.webp',
    images: ['/templates/preview-pharmacy/prm-prod-baume-mains.webp'],
  }),
  prod({
    id: 'ph-prod-4',
    name: 'Thermomètre frontal sans contact',
    slug: 'ph-vital-thermometre-frontal-sans-contact',
    price: 89,
    category_id: PH3,
    product_type: 'other',
    image: '/templates/preview-pharmacy/prm-prod-thermo.webp',
    images: ['/templates/preview-pharmacy/prm-prod-thermo.webp'],
  }),
  prod({
    id: 'ph-prod-5',
    name: 'Tensiomètre brassard',
    slug: 'ph-vital-tensiometre-brassard',
    price: 199,
    category_id: PH3,
    product_type: 'other',
    image: '/templates/preview-pharmacy/prm-prod-tensio.webp',
    images: ['/templates/preview-pharmacy/prm-prod-tensio.webp'],
  }),
  prod({
    id: 'ph-prod-6',
    name: 'Liniment oléo-calcaire 750 ml',
    slug: 'ph-vital-liniment-oleo-calcaire',
    price: 18,
    category_id: PH4,
    product_type: 'other',
    image: '/templates/preview-pharmacy/prm-prod-liniment.webp',
    images: ['/templates/preview-pharmacy/prm-prod-liniment.webp'],
  }),
  prod({
    id: 'ph-prod-7',
    name: 'Couches hypoallergéniques T3',
    slug: 'ph-vital-couches-hypoallergeniques-t3',
    price: 54,
    category_id: PH4,
    product_type: 'other',
    image: '/templates/preview-pharmacy/prm-prod-couches.webp',
    images: ['/templates/preview-pharmacy/prm-prod-couches.webp'],
  }),
  prod({
    id: 'ph-prod-8',
    name: 'Gel douche surgras 400 ml',
    slug: 'ph-vital-gel-douche-surgras',
    price: 16,
    category_id: PH1,
    product_type: 'other',
    image: '/templates/preview-pharmacy/prm-prod-gel-douche.webp',
    images: ['/templates/preview-pharmacy/prm-prod-gel-douche.webp'],
  }),
]

// ─── Perfume (footer slugs) ──────────────────────────────────────────────────

const PF1 = 'pf000001-0000-4000-8000-000000000001'
const PF2 = 'pf000001-0000-4000-8000-000000000002'
const PF3 = 'pf000001-0000-4000-8000-000000000003'
const PF4 = 'pf000001-0000-4000-8000-000000000004'

export const demoCategoriesPerfume: Category[] = [
  cat({ id: PF1, name: 'Parfums homme', slug: 'homme', position: 1, image_url: '/templates/premium-cosmetics-v1/extrait-ombre-de-figuier-custom-800.webp' }),
  cat({ id: PF2, name: 'Parfums femme', slug: 'femme', position: 2, image_url: '/templates/premium-cosmetics-v1/eau-de-parfum-rose-orientale-custom-800.webp' }),
  cat({ id: PF3, name: 'Oud & oriental', slug: 'oud', position: 3, image_url: '/templates/premium-cosmetics-v1/huile-rose-musquee-custom-800.webp' }),
  cat({ id: PF4, name: 'Bougies parfumées', slug: 'bougies', position: 4, image_url: '/templates/premium-cosmetics-v1/photophores-verre-depoli-custom-800.webp' }),
]

export const demoProductsPerfume: Product[] = [
  prod({
    id: 'pf-prod-1',
    name: 'EDP — Atlas Cedar 100 ml',
    slug: 'pf-sillage-edp-atlas-cedar-100',
    price: 189,
    category_id: PF1,
    product_type: 'cosmetics',
    image: '/templates/premium-cosmetics-v1/extrait-ombre-de-figuier-custom-800.webp',
    images: ['/templates/premium-cosmetics-v1/extrait-ombre-de-figuier-custom-800.webp'],
  }),
  prod({
    id: 'pf-prod-2',
    name: 'EDP — Rose de Tunis 50 ml',
    slug: 'pf-sillage-edp-rose-tunis-50',
    price: 165,
    category_id: PF2,
    product_type: 'cosmetics',
    image: '/templates/premium-cosmetics-v1/eau-de-parfum-rose-orientale-custom-800.webp',
    images: ['/templates/premium-cosmetics-v1/eau-de-parfum-rose-orientale-custom-800.webp'],
  }),
  prod({
    id: 'pf-prod-3',
    name: 'Oud Cambodi — huile pure 12 ml',
    slug: 'pf-sillage-oud-cambodi-huile-12',
    price: 220,
    category_id: PF3,
    product_type: 'cosmetics',
    image: '/templates/premium-cosmetics-v1/huile-rose-musquee-custom-800.webp',
    images: ['/templates/premium-cosmetics-v1/huile-rose-musquee-custom-800.webp'],
  }),
  prod({
    id: 'pf-prod-4',
    name: 'Bougie cèdre & figue — 220 g',
    slug: 'pf-sillage-bougie-cedre-figue-220',
    price: 48,
    category_id: PF4,
    product_type: 'cosmetics',
    image: '/templates/premium-cosmetics-v1/photophores-verre-depoli-custom-800.webp',
    images: ['/templates/premium-cosmetics-v1/photophores-verre-depoli-custom-800.webp'],
  }),
  prod({
    id: 'pf-prod-5',
    name: 'Bakhour maison — boîte 40 g',
    slug: 'pf-sillage-bakhour-maison-40',
    price: 32,
    category_id: PF3,
    product_type: 'cosmetics',
    image: '/templates/premium-cosmetics-v1/editorial-collection-custom-800.webp',
    images: ['/templates/premium-cosmetics-v1/editorial-collection-custom-800.webp'],
  }),
  prod({
    id: 'pf-prod-6',
    name: 'Brume cheveux — jasmin',
    slug: 'pf-sillage-brume-cheveux-jasmin',
    price: 39,
    category_id: PF2,
    product_type: 'cosmetics',
    image: '/templates/premium-cosmetics-v1/brume-cheveux-tubereuse-custom.webp',
    images: ['/templates/premium-cosmetics-v1/brume-cheveux-tubereuse-custom.webp'],
  }),
  prod({
    id: 'pf-prod-7',
    name: 'Coffret découverte 4 x 15 ml',
    slug: 'pf-sillage-coffret-decouverte-4x15',
    price: 95,
    category_id: PF2,
    product_type: 'cosmetics',
    image: '/templates/premium-cosmetics-v1/editorial-gallery-2-custom-800.webp',
    images: ['/templates/premium-cosmetics-v1/editorial-gallery-2-custom-800.webp'],
  }),
  prod({
    id: 'pf-prod-8',
    name: 'Aftershave baume — aloe',
    slug: 'pf-sillage-aftershave-baume-aloe',
    price: 42,
    category_id: PF1,
    product_type: 'cosmetics',
    image: '/templates/premium-cosmetics-v1/baume-premier-barriere-ceramide-custom-800.webp',
    images: ['/templates/premium-cosmetics-v1/baume-premier-barriere-ceramide-custom-800.webp'],
  }),
]

// ─── General store ───────────────────────────────────────────────────────────

const GN1 = 'gn000001-0000-4000-8000-000000000001'
const GN2 = 'gn000001-0000-4000-8000-000000000002'
const GN3 = 'gn000001-0000-4000-8000-000000000003'
const GN4 = 'gn000001-0000-4000-8000-000000000004'

export const demoCategoriesGeneral: Category[] = [
  cat({ id: GN1, name: 'Promotions', slug: 'promotions', position: 1, image_url: '/templates/local-webp/promo-winter.webp' }),
  cat({ id: GN2, name: 'Nouveautés', slug: 'nouveautes', position: 2, image_url: '/templates/local-webp/promo-new.webp' }),
  cat({ id: GN3, name: 'Maison & quotidien', slug: 'maison-quotidien', position: 3, image_url: '/templates/local-webp/promo-packs.webp' }),
  cat({ id: GN4, name: 'Idées cadeaux', slug: 'idees-cadeaux', position: 4, image_url: '/templates/local-webp/promo-members.webp' }),
]

export const demoProductsGeneral: Product[] = [
  prod({
    id: 'gen-prod-1',
    name: 'Pack accessoires bureau',
    slug: 'gn-mart-pack-accessoires-bureau',
    price: 49,
    category_id: GN3,
    product_type: 'other',
    image: '/templates/local-webp/pack-bureau.webp',
    images: ['/templates/local-webp/pack-bureau.webp'],
  }),
  prod({
    id: 'gen-prod-2',
    name: 'Lampe LED rechargeable',
    slug: 'gn-mart-lampe-led-rechargeable',
    price: 65,
    category_id: GN3,
    product_type: 'other',
    image: '/templates/dropship-velocity-v1/drp-lampe-bureau-led-pir-800.webp',
    images: ['/templates/dropship-velocity-v1/drp-lampe-bureau-led-pir-800.webp'],
  }),
  prod({
    id: 'gen-prod-3',
    name: 'Enceinte Bluetooth portable',
    slug: 'gn-mart-enceinte-bluetooth-portable',
    price: 129,
    compare_at_price: 159,
    category_id: GN1,
    product_type: 'other',
    image: '/templates/local-webp/elx-prod-6.webp',
    images: ['/templates/local-webp/elx-prod-6.webp'],
  }),
  prod({
    id: 'gen-prod-4',
    name: 'Montre connectée — graphite',
    slug: 'gn-mart-montre-connectee-graphite',
    price: 289,
    category_id: GN2,
    product_type: 'other',
    image: '/templates/editorial-fashion-v1/vis-edt-06-800.webp',
    images: ['/templates/editorial-fashion-v1/vis-edt-06-800.webp'],
  }),
  prod({
    id: 'gen-prod-5',
    name: 'Coffret bien-être 5 pièces',
    slug: 'gn-mart-coffret-bien-etre-5',
    price: 72,
    category_id: GN4,
    product_type: 'other',
    image: '/templates/local-webp/bty-story-rituel.webp',
    images: ['/templates/local-webp/bty-story-rituel.webp'],
  }),
  prod({
    id: 'gen-prod-6',
    name: 'Bouteille isotherme 750 ml',
    slug: 'gn-mart-bouteille-isotherme-750',
    price: 38,
    category_id: GN2,
    product_type: 'other',
    image: '/templates/premium-cosmetics-v1/carafe-gres-emaille-custom.webp',
    images: ['/templates/premium-cosmetics-v1/carafe-gres-emaille-custom.webp'],
  }),
  prod({
    id: 'gen-prod-7',
    name: 'Casque gaming filaire',
    slug: 'gn-mart-casque-gaming-filaire',
    price: 159,
    category_id: GN1,
    product_type: 'other',
    image: '/templates/local-webp/elx-prod-7.webp',
    images: ['/templates/local-webp/elx-prod-7.webp'],
  }),
  prod({
    id: 'gen-prod-8',
    name: 'Drone mini pliable',
    slug: 'gn-mart-drone-mini-pliable',
    price: 349,
    category_id: GN2,
    product_type: 'other',
    image: '/templates/premium-electronics-v1/hero-800.webp',
    images: ['/templates/premium-electronics-v1/hero-800.webp'],
  }),
]

// ─── Dropship (unique slugs vs electronics) ─────────────────────────────────

const DS1 = 'ds000001-0000-4000-8000-000000000001'
const DS2 = 'ds000001-0000-4000-8000-000000000002'
const DS3 = 'ds000001-0000-4000-8000-000000000003'
const DS4 = 'ds000001-0000-4000-8000-000000000004'

export const demoCategoriesDropship: Category[] = [
  cat({ id: DS1, name: 'Flash tech', slug: 'flash-tech', position: 1, image_url: '/templates/dropship-velocity-v1/promo-gadget-coffre-tech-800.webp' }),
  cat({ id: DS2, name: 'Maison pratique', slug: 'maison-pratique', position: 2, image_url: '/templates/dropship-velocity-v1/promo-home-rangements-800.webp' }),
  cat({ id: DS3, name: 'Soins tendance', slug: 'soins-tendance', position: 3, image_url: '/templates/dropship-velocity-v1/promo-skincare-marble-800.webp' }),
  cat({ id: DS4, name: 'Sport & récup', slug: 'sport-recup', position: 4, image_url: '/templates/dropship-velocity-v1/promo-fitness-recovery-800.webp' }),
]

export const demoProductsDropship: Product[] = [
  prod({
    id: 'drp-prod-1',
    name: 'Kit QuickShip USB-C',
    slug: 'velocity-hub-usbc-7en1',
    price: 59,
    category_id: DS1,
    product_type: 'electronics',
    image: '/templates/dropship-velocity-v1/drp-kit-quickship-usbc-800.webp',
    images: ['/templates/dropship-velocity-v1/drp-kit-quickship-usbc-800.webp'],
  }),
  prod({
    id: 'drp-prod-2',
    name: 'Lampe de bureau LED — PIR',
    slug: 'velocity-lampe-bureau-led-pir',
    price: 44,
    category_id: DS2,
    product_type: 'other',
    image: '/templates/dropship-velocity-v1/drp-lampe-bureau-led-pir-800.webp',
    images: ['/templates/dropship-velocity-v1/drp-lampe-bureau-led-pir-800.webp'],
  }),
  prod({
    id: 'drp-prod-3',
    name: 'Flash Deal soin visage',
    slug: 'velocity-rouleau-visage-quartz',
    price: 19,
    category_id: DS3,
    product_type: 'cosmetics',
    image: '/templates/dropship-velocity-v1/drp-flash-deal-soin-visage-800.webp',
    images: ['/templates/dropship-velocity-v1/drp-flash-deal-soin-visage-800.webp'],
  }),
  prod({
    id: 'drp-prod-4',
    name: 'Box fitness express',
    slug: 'velocity-bandes-elastiques-fitness',
    price: 29,
    category_id: DS4,
    product_type: 'other',
    image: '/templates/dropship-velocity-v1/velocity-bandes-elastiques-fitness-800.webp',
    images: ['/templates/dropship-velocity-v1/velocity-bandes-elastiques-fitness-800.webp'],
  }),
  prod({
    id: 'drp-prod-5',
    name: 'Mini brise ventilateur',
    slug: 'velocity-mini-ventilateur-usb',
    price: 24,
    category_id: DS1,
    product_type: 'electronics',
    image: '/templates/dropship-velocity-v1/drp-mini-ventilateur-usb-800.webp',
    images: ['/templates/dropship-velocity-v1/drp-mini-ventilateur-usb-800.webp'],
  }),
  prod({
    id: 'drp-prod-6',
    name: 'Organisateur tiroir modulable',
    slug: 'velocity-organisateur-tiroir',
    price: 34,
    category_id: DS2,
    product_type: 'other',
    image: '/templates/dropship-velocity-v1/drp-organisateur-tiroir-800.webp',
    images: ['/templates/dropship-velocity-v1/drp-organisateur-tiroir-800.webp'],
  }),
  prod({
    id: 'drp-prod-7',
    name: 'Gua sha inox flash',
    slug: 'velocity-gua-sha-inox',
    price: 16,
    category_id: DS3,
    product_type: 'cosmetics',
    image: '/templates/dropship-velocity-v1/drp-gua-sha-inox-flash-800.webp',
    images: ['/templates/dropship-velocity-v1/drp-gua-sha-inox-flash-800.webp'],
  }),
  prod({
    id: 'drp-prod-8',
    name: 'Tapis yoga pliable 6 mm',
    slug: 'velocity-tapis-yoga-pliable',
    price: 79,
    category_id: DS4,
    product_type: 'other',
    image: '/templates/dropship-velocity-v1/velocity-tapis-yoga-pliable-800.webp',
    images: ['/templates/dropship-velocity-v1/velocity-tapis-yoga-pliable-800.webp'],
  }),
]

// ─── Funnel mono-SKU focus ───────────────────────────────────────────────────

const FN1 = 'fn000001-0000-4000-8000-000000000001'

export const demoCategoriesFunnel: Category[] = [
  cat({
    id: FN1,
    name: 'Référence fondateur',
    slug: 'fondateur',
    position: 1,
    description: 'Tunnel mono-SKU — produits complémentaires',
    image_url: '/templates/single-product-funnel-v1/funnel-05-800.webp',
  }),
]

export const demoProductsFunnel: Product[] = [
  prod({
    id: 'fn-prod-1',
    name: 'Unité Core One — édition fondateur',
    slug: 'fn-prod-1',
    price: 1299,
    compare_at_price: 1499,
    category_id: FN1,
    product_type: 'electronics',
    image: '/templates/single-product-funnel-v1/funnel-01-800.webp',
    images: ['/templates/single-product-funnel-v1/funnel-01-800.webp'],
  }),
  prod({
    id: 'fn-prod-2',
    name: 'Pack Core — graphite',
    slug: 'fn-prod-2',
    price: 2200,
    category_id: FN1,
    product_type: 'electronics',
    image: '/templates/single-product-funnel-v1/funnel-02-800.webp',
    images: ['/templates/single-product-funnel-v1/funnel-02-800.webp'],
  }),
  prod({
    id: 'fn-prod-3',
    name: 'Duo Core — série limitée',
    slug: 'fn-prod-3',
    price: 3600,
    category_id: FN1,
    product_type: 'electronics',
    image: '/templates/single-product-funnel-v1/funnel-03-800.webp',
    images: ['/templates/single-product-funnel-v1/funnel-03-800.webp'],
  }),
]

// ─── Home classic (`home-decor` template slugs) ──────────────────────────────

const HC1 = 'hc000001-0000-4000-8000-000000000001'
const HC2 = 'hc000001-0000-4000-8000-000000000002'
const HC3 = 'hc000001-0000-4000-8000-000000000003'
const HC4 = 'hc000001-0000-4000-8000-000000000004'

export const demoCategoriesHomeClassic: Category[] = [
  cat({ id: HC1, name: 'Salon', slug: 'salon', position: 1, image_url: '/templates/home-decor-pinterest-v1/home-086-800.webp' }),
  cat({ id: HC2, name: 'Chambre', slug: 'chambre', position: 2, image_url: '/templates/home-decor-pinterest-v1/home-082-800.webp' }),
  cat({ id: HC3, name: 'Cuisine', slug: 'cuisine', position: 3, image_url: '/templates/home-decor-pinterest-v1/home-037-800.webp' }),
  cat({ id: HC4, name: 'Bougies & senteurs', slug: 'bougies', position: 4, image_url: '/templates/home-decor-pinterest-v1/carre-011-800.webp' }),
]

export const demoProductsHomeClassic: Product[] = [
  prod({
    id: 'hc-prod-1',
    name: 'Canapé lin 3 places — sable',
    slug: 'hc-haven-canape-lin-3-sable',
    price: 2890,
    category_id: HC1,
    product_type: 'home',
    image: '/templates/home-decor-pinterest-v1/home-047-800.webp',
    images: ['/templates/home-decor-pinterest-v1/home-047-800.webp'],
  }),
  prod({
    id: 'hc-prod-2',
    name: 'Table basse travertin',
    slug: 'hc-haven-table-basse-travertin',
    price: 890,
    category_id: HC1,
    product_type: 'home',
    image: '/templates/home-decor-pinterest-v1/home-052-800.webp',
    images: ['/templates/home-decor-pinterest-v1/home-052-800.webp'],
  }),
  prod({
    id: 'hc-prod-3',
    name: 'Tête de lit cannage',
    slug: 'hc-haven-tete-lit-cannage',
    price: 420,
    category_id: HC2,
    product_type: 'home',
    image: '/templates/home-decor-pinterest-v1/home-071-800.webp',
    images: ['/templates/home-decor-pinterest-v1/home-071-800.webp'],
  }),
  prod({
    id: 'hc-prod-4',
    name: 'Parure percale — ardoise',
    slug: 'hc-haven-parure-percale-ardoise',
    price: 165,
    category_id: HC2,
    product_type: 'home',
    image: '/templates/home-decor-pinterest-v1/home-064-800.webp',
    images: ['/templates/home-decor-pinterest-v1/home-064-800.webp'],
  }),
  prod({
    id: 'hc-prod-5',
    name: 'Set couteaux céramique',
    slug: 'hc-haven-set-couteaux-ceramique',
    price: 198,
    category_id: HC3,
    product_type: 'home',
    image: '/templates/home-decor-pinterest-v1/home-034-800.webp',
    images: ['/templates/home-decor-pinterest-v1/home-034-800.webp'],
  }),
  prod({
    id: 'hc-prod-6',
    name: 'Planche à découper chêne',
    slug: 'hc-haven-planche-decouper-chene',
    price: 54,
    category_id: HC3,
    product_type: 'home',
    image: '/templates/home-decor-pinterest-v1/home-030-800.webp',
    images: ['/templates/home-decor-pinterest-v1/home-030-800.webp'],
  }),
  prod({
    id: 'hc-prod-7',
    name: 'Bougie parfumée — figue noire',
    slug: 'hc-haven-bougie-figue-noire',
    price: 42,
    category_id: HC4,
    product_type: 'home',
    image: '/templates/home-decor-pinterest-v1/carre-005-800.webp',
    images: ['/templates/home-decor-pinterest-v1/carre-005-800.webp'],
  }),
  prod({
    id: 'hc-prod-8',
    name: 'Diffuseur rotin — bergamote',
    slug: 'hc-haven-diffuseur-rotin-bergamote',
    price: 58,
    category_id: HC4,
    product_type: 'home',
    image: '/templates/home-decor-pinterest-v1/carre-013-800.webp',
    images: ['/templates/home-decor-pinterest-v1/carre-013-800.webp'],
  }),
]

// ─── Premium tech (same block productIds prod-* — distinct titles vs electronics) ─

const PT_PH = 'cccccccc-cccc-4ccc-8ccc-cccccccccc01'
const PT_LP = 'cccccccc-cccc-4ccc-8ccc-cccccccccc02'
const PT_AU = 'cccccccc-cccc-4ccc-8ccc-cccccccccc03'
const PT_SM = 'cccccccc-cccc-4ccc-8ccc-cccccccccc04'

export const demoCategoriesPremiumTech: Category[] = [
  {
    id: PT_PH,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    // Dedicated slug for coques & accessoires only — not `smartphones` (reads like handsets, not cases).
    name: 'Cases & accessories',
    slug: 'phone-cases',
    description: 'Coques, protections et accessoires téléphone (démo preview)',
    image_url: '/templates/local-webp/elx-cat-phones.webp',
    position: 1,
    is_active: true,
    created_at: NOW,
    updated_at: NOW,
    product_count: 3,
  },
  {
    id: PT_LP,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Laptops',
    slug: 'laptops-tablets',
    description: 'Laptops and pro machines (preview catalog)',
    image_url: '/templates/local-webp/elx-cat-laptops.webp',
    position: 2,
    is_active: true,
    created_at: NOW,
    updated_at: NOW,
    product_count: 2,
  },
  {
    id: PT_AU,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Audio',
    slug: 'audio',
    description: 'AirPods, buds, bands, and sound (preview catalog)',
    image_url: '/templates/local-webp/elx-cat-audio.webp',
    position: 3,
    is_active: true,
    created_at: NOW,
    updated_at: NOW,
    product_count: 2,
  },
  {
    id: PT_SM,
    store_id: DEMO_STORE_ID,
    parent_id: null,
    name: 'Smart home',
    slug: 'smart-home',
    description: 'Security and lighting',
    image_url: '/templates/local-webp/elx-cat-smart.webp',
    position: 4,
    is_active: true,
    created_at: NOW,
    updated_at: NOW,
    product_count: 1,
  },
]

export const demoProductsPremiumTech: Product[] = [
  {
    id: 'pt-prod-1',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Nexa Fold Shield — aramid ink',
    slug: 'nexa-pt-fold-shield-aramid-ink',
    price: 4299,
    compare_at_price: 4599,
    stock: 14,
    status: 'active',
    image: '/templates/local-webp/elx-prod-1.webp',
    images: ['/templates/local-webp/elx-prod-1.webp'],
    orders_count: 312,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PT_PH,
    product_type: 'electronics',
  },
  {
    id: 'pt-prod-2',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Nexa Flip Armor — graphite',
    slug: 'nexa-pt-flip-armor-graphite',
    price: 5499,
    compare_at_price: null,
    stock: 6,
    status: 'active',
    image: '/templates/local-webp/elx-prod-2.webp',
    images: ['/templates/local-webp/elx-prod-2.webp'],
    orders_count: 198,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PT_PH,
    product_type: 'electronics',
  },
  {
    id: 'pt-prod-3',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'Nexa Flip Pop — mint frost',
    slug: 'nexa-pt-flip-pop-mint-frost',
    price: 2899,
    stock: 22,
    status: 'active',
    image: '/templates/local-webp/elx-prod-3.webp',
    images: ['/templates/local-webp/elx-prod-3.webp'],
    orders_count: 267,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PT_PH,
    product_type: 'electronics',
  },
  {
    id: 'pt-prod-4',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'VoltBook Pro 15 — OLED glass',
    slug: 'nexa-pt-voltbook-pro-15-oled-glass',
    price: 3999,
    compare_at_price: 4499,
    stock: 9,
    status: 'active',
    image: '/templates/local-webp/elx-prod-4.webp',
    images: ['/templates/local-webp/elx-prod-4.webp'],
    orders_count: 156,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PT_LP,
    product_type: 'electronics',
  },
  {
    id: 'pt-prod-5',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'VoltBook Air 14 — aluminium',
    slug: 'nexa-pt-voltbook-air-14-aluminium',
    price: 3299,
    stock: 11,
    status: 'active',
    image: '/templates/local-webp/elx-prod-5.webp',
    images: ['/templates/local-webp/elx-prod-5.webp'],
    orders_count: 124,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PT_LP,
    product_type: 'electronics',
  },
  {
    id: 'pt-prod-6',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'PulseBuds Pro — ANC 2.0',
    slug: 'nexa-pt-pulsebuds-pro-anc-2',
    price: 449,
    compare_at_price: 599,
    stock: 48,
    status: 'active',
    image: '/templates/local-webp/elx-prod-6.webp',
    images: ['/templates/local-webp/elx-prod-6.webp'],
    orders_count: 401,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PT_AU,
    product_type: 'electronics',
  },
  {
    id: 'pt-prod-7',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'PulseStudio over-ear',
    slug: 'nexa-pt-pulsestudio-over-ear',
    price: 799,
    stock: 18,
    status: 'active',
    image: '/templates/local-webp/elx-prod-7.webp',
    images: ['/templates/local-webp/elx-prod-7.webp'],
    orders_count: 89,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PT_AU,
    product_type: 'electronics',
  },
  {
    id: 'pt-prod-8',
    user_id: '__preview__',
    store_id: DEMO_STORE_ID,
    name: 'HomeCam 2K — night vision',
    slug: 'nexa-pt-homecam-2k-night-vision',
    price: 329,
    stock: 35,
    status: 'active',
    image: '/templates/local-webp/elx-prod-8.webp',
    images: ['/templates/local-webp/elx-prod-8.webp'],
    orders_count: 210,
    meta_title: null,
    meta_description: null,
    created_at: NOW,
    updated_at: NOW,
    has_variants: false,
    warehouse_id: null,
    category_id: PT_SM,
    product_type: 'electronics',
  },
]

/** Premium-tech preview catalog — ids are `pt-prod-*` (globally unique vs other preview verticals). */
export const DEMO_PREMIUM_TECH_SPOTLIGHT_ID = 'pt-prod-1'
export const DEMO_PREMIUM_TECH_BUNDLE_IDS: readonly string[] = ['pt-prod-1', 'pt-prod-2', 'pt-prod-3']

/** Slugs from `demoCategoriesPremiumTech` — use in `futurerPremiumTemplate` so PLPs never drift from preview data. */
export const PREMIUM_TECH_CATEGORY_SLUGS: readonly string[] = demoCategoriesPremiumTech.map((c) => c.slug)

/** Use for every `slug` in premium-tech layout JSON — throws in DEV if the slug is not in the demo catalog. */
export function premiumTechCategorySlug(slug: string): string {
  const s = slug.trim()
  if (!PREMIUM_TECH_CATEGORY_SLUGS.includes(s)) {
    if (import.meta.env.DEV) {
      throw new Error(
        `[preview] Unknown premium-tech category slug "${slug}". Expected one of: ${PREMIUM_TECH_CATEGORY_SLUGS.join(', ')}`,
      )
    }
  }
  return s
}

if (import.meta.env.DEV) {
  const pairs: { label: string; categories: Category[]; products: Product[] }[] = [
    { label: 'fashion', categories: demoCategoriesFashion, products: demoProductsFashion },
    { label: 'fashionV2', categories: demoCategoriesFashionV2, products: demoProductsFashionV2 },
    { label: 'editorialFashion', categories: demoCategoriesEditorial, products: demoProductsEditorial },
    { label: 'editorialSleek', categories: demoCategoriesEditorialSleek, products: demoProductsEditorialSleek },
    { label: 'food', categories: demoCategoriesFood, products: demoProductsFood },
    { label: 'kids', categories: demoCategoriesKids, products: demoProductsKids },
    { label: 'pharmacy', categories: demoCategoriesPharmacy, products: demoProductsPharmacy },
    { label: 'perfume', categories: demoCategoriesPerfume, products: demoProductsPerfume },
    { label: 'general', categories: demoCategoriesGeneral, products: demoProductsGeneral },
    { label: 'dropship', categories: demoCategoriesDropship, products: demoProductsDropship },
    { label: 'funnel', categories: demoCategoriesFunnel, products: demoProductsFunnel },
    { label: 'homeClassic', categories: demoCategoriesHomeClassic, products: demoProductsHomeClassic },
    { label: 'premiumTech', categories: demoCategoriesPremiumTech, products: demoProductsPremiumTech },
  ]
  for (const { label, categories, products } of pairs) {
    assertPreviewVerticalProductCategoryCohesion(label, categories, products)
  }
}
